import React, { useEffect, useRef } from 'react';

export type PreviewKind = 'vehicle' | 'shop' | 'club' | 'event' | 'campaign';

// Web adaptation of native revealVariants.ts + domain/revealVariant.ts.
// These examples represent an added Rare build, Common shop and Uncommon event.
export const previewRevealProfiles = {
  vehicle: { colors: ['#38BDF8', '#22D3EE', '#E0F2FE', '#FFFFFF', '#4ADE80', '#FBBF24'], count: 12, floats: 6, blooms: 3, gravity: 260, lifetime: 2700, spread: 1.15, speedMin: 220 * 1.0444, speedMax: 520 * 1.068, sizeMin: 5.5 * 1.32, sizeMax: 12 * 1.32, aura: .78 * 1.28, sheen: 3600 },
  shop: { colors: ['#FDA4AF', '#FECDD3', '#FFFFFF', '#E7E5E4'], count: 10, floats: 5, blooms: 2, gravity: 220, lifetime: 2300, spread: .9, speedMin: 140 * 1.03, speedMax: 380 * 1.05, sizeMin: 5.5 * 1.15, sizeMax: 11 * 1.15, aura: .7 * 1.1, sheen: 4800 },
  club: { colors: ['#C026D3', '#E879F9', '#7C3AED', '#F5D0FE', '#FFFFFF'], count: 11, floats: 5, blooms: 2, gravity: 160, lifetime: 2500, spread: .95, speedMin: 110 * 1.03, speedMax: 340 * 1.05, sizeMin: 5.5 * 1.15, sizeMax: 12 * 1.15, aura: .8 * 1.12, sheen: 5000 },
  event: { colors: ['#F472B6', '#A78BFA', '#34D399', '#FBBF24', '#FFFFFF', '#60A5FA'], count: 12, floats: 5, blooms: 3, gravity: 250, lifetime: 2562, spread: 1.2, speedMin: 200 * 1.0364, speedMax: 500 * 1.058, sizeMin: 6 * 1.2, sizeMax: 13 * 1.2, aura: .8 * 1.18, sheen: 4200 },
  campaign: { colors: ['#FBBF24', '#F59E0B', '#FEF3C7', '#FFFFFF', '#D97706'], count: 12, floats: 6, blooms: 3, gravity: 280, lifetime: 2600, spread: 1.05, speedMin: 200 * 1.04, speedMax: 480 * 1.06, sizeMin: 6 * 1.2, sizeMax: 13 * 1.2, aura: .82 * 1.18, sheen: 3900 },
} as const;

function randomSeed(seed: number) {
  let state = seed;
  return () => { state = state * 16807 % 2147483647; return state % 10000 / 10000; };
}
const clamp = (value: number) => Math.max(0, Math.min(1, value));

/** Dimmed camera → card rise → settle burst, soft shockwave, bloom and ambient motes. */
export default function PartnerRevealEffects({ kind, active }: { kind: PreviewKind; active: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const elapsed = useRef(0);
  useEffect(() => {
    const node = canvas.current;
    if (!node || !active) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    let previous = 0;
    const profile = previewRevealProfiles[kind];
    const rand = randomSeed(11);
    const sparks = Array.from({ length: profile.count }, (_, i) => ({
      angle: -Math.PI / 2 + (rand() - .5) * Math.PI * profile.spread,
      speed: profile.speedMin + rand() * (profile.speedMax - profile.speedMin),
      size: profile.sizeMin + rand() * (profile.sizeMax - profile.sizeMin),
      color: profile.colors[i % profile.colors.length], wobble: (rand() - .5) * 28,
      delay: rand() * .22, drag: .9 + rand() * .08,
      glow: .75 + rand() * .35,
    }));
    const floatRand = randomSeed(97);
    const floats = Array.from({ length: profile.floats }, (_, i) => ({
      x: .12 + floatRand() * .76, y: .28 + floatRand() * .44,
      ampX: 6 + floatRand() * 16, ampY: 10 + floatRand() * 22,
      phase: floatRand() * Math.PI * 2, speed: .32 + floatRand() * .42,
      size: 5.5 + floatRand() * 4.5, color: profile.colors[i % profile.colors.length],
      opacity: .16 + floatRand() * .2,
    }));
    const bloomRand = randomSeed(41);
    const blooms = Array.from({ length: profile.blooms }, (_, i) => ({
      angle: -Math.PI / 2 + (bloomRand() - .5) * Math.PI * .65,
      speed: profile.speedMin * (.28 + bloomRand() * .38),
      size: profile.sizeMax * (2.6 + bloomRand() * 1.8),
      color: profile.colors[i % profile.colors.length], delay: .01 + bloomRand() * .1,
    }));
    const draw = (now: number) => {
      if (reduced.matches) return;
      const ctx = node.getContext('2d');
      if (!ctx) return;
      elapsed.current += previous ? Math.min(now - previous, 64) : 0;
      previous = now;
      const t = elapsed.current;
      const { width, height } = node.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (node.width !== Math.round(width * dpr) || node.height !== Math.round(height * dpr)) {
        node.width = Math.round(width * dpr); node.height = Math.round(height * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      const orb = (x: number, y: number, radius: number, color: string, opacity: number, hot = false) => {
        if (opacity <= 0 || radius <= 0) return;
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
        gradient.addColorStop(0, hot ? '#ffffff' : color);
        gradient.addColorStop(.32, color);
        gradient.addColorStop(1, `${color}00`);
        ctx.globalAlpha = clamp(opacity); ctx.fillStyle = gradient;
        ctx.beginPath(); ctx.arc(x, y, radius, 0, Math.PI * 2); ctx.fill();
      };
      // Native effects use the full scan screen; anchor to the smaller preview card here.
      const card = node.parentElement?.querySelector<HTMLElement>('.partner-phone-card');
      const x = width * .5;
      const y = card ? card.offsetTop + card.offsetHeight * .62 : height * .43;
      const pulse = .91 + Math.sin(t / 3200 * Math.PI) * .09;
      orb(x, y + 18, width * .5, profile.colors[kind === 'event' || kind === 'club' ? 1 : 0], profile.aura * .55 * pulse * clamp(t / 480));
      // The native spring settles before its 1580ms fallback; the web spring is 1000ms.
      const progress = clamp((t - 1000) / profile.lifetime);
      if (progress > 0 && progress < 1) {
        const eased = 1 - Math.pow(1 - progress, 2.4);
        const ringRadius = 18 + width * .52 * eased;
        const gradient = ctx.createRadialGradient(x, y, ringRadius * .42, x, y, ringRadius);
        gradient.addColorStop(0, `${profile.colors[0]}00`);
        gradient.addColorStop(.45, `${profile.colors[0]}88`);
        gradient.addColorStop(1, `${profile.colors[0]}00`);
        ctx.globalAlpha = .55 * Math.sin(progress * Math.PI); ctx.fillStyle = gradient;
        ctx.beginPath(); ctx.arc(x, y, ringRadius, 0, Math.PI * 2); ctx.fill();
        blooms.forEach(seed => {
          const local = clamp((progress - seed.delay) / (1 - seed.delay));
          const sec = (1 - Math.pow(1 - local, 2.4)) * profile.lifetime / 1000 * .75;
          orb(x + Math.cos(seed.angle) * seed.speed * sec, y + Math.sin(seed.angle) * seed.speed * sec,
            seed.size * (.45 + local * 1.05), seed.color, .48 * Math.sin(local * Math.PI));
        });
        sparks.forEach(seed => {
          const local = clamp((progress - seed.delay) / (1 - seed.delay));
          const sec = local * profile.lifetime / 1000, damp = Math.pow(seed.drag, sec * 7);
          const opacity = local < .1 ? local / .1 : local < .4 ? 1 : Math.pow(1 - (local - .4) / .6, 1.6);
          orb(x + Math.cos(seed.angle) * seed.speed * sec * damp + seed.wobble * local,
            y + Math.sin(seed.angle) * seed.speed * sec * damp + .5 * profile.gravity * sec * sec,
            seed.size * (1.09 - local * .5), seed.color, opacity, true);
        });
      }
      if (t > 560) floats.forEach(seed => {
        const clock = (t - 560) / 12000 * Math.PI * 2;
        orb(width * seed.x + Math.sin(clock * seed.speed + seed.phase) * seed.ampX,
          height * seed.y + Math.cos(clock * seed.speed * .72 + seed.phase) * seed.ampY,
          seed.size, seed.color, seed.opacity * (.62 + .38 * Math.sin(clock + seed.phase)), true);
      });
      ctx.globalAlpha = 1;
      node.dataset.frame = String(Math.round(t));
      frame = requestAnimationFrame(draw);
    };
    const start = () => {
      cancelAnimationFrame(frame); previous = 0;
      if (!reduced.matches) frame = requestAnimationFrame(draw);
    };
    start(); reduced.addEventListener('change', start);
    return () => { cancelAnimationFrame(frame); reduced.removeEventListener('change', start); };
  }, [active, kind]);
  return <canvas ref={canvas} className="partner-reveal-effects" aria-hidden="true" data-kind={kind} />;
}
